import type {
  AdmDocumentContext,
  AdmDocumentMetadata,
  AdmFileUploadResponse,
  AdmCreatedDocumentResponse,
  AdmTagValuePair,
} from "@/types/adm-document";
import {
  ADM_FOLDER_ID,
  ADM_TAG_IDS,
  ADM_TAG_NAMES,
} from "@/types/adm-document";
import { getAdmAuthHeaders } from "@/services/adm-upload-service";

// ---------------------------------------------------------------------------
// Purchase-Request ADM constants
// ---------------------------------------------------------------------------
export const PR_ADM_DOC_TYPE_ID = 29;
export const PR_ADM_LINK_TYPE = "PurchaseRequestRecord";
export const PR_ADM_LINK_PRODUCT = "Access Dimensions";
export const PR_ADM_KEY_TAG_ID = ADM_TAG_IDS.HEADER_REF;
export const PR_ADM_KEY_TAG_NAME = ADM_TAG_NAMES.HEADER_REF;

// ---------------------------------------------------------------------------
// Mapped document type returned to the UI
// ---------------------------------------------------------------------------
export interface PurchaseRequestDocument {
  id: string;
  fileName: string;
  fileType: string;
  documentType: string;
  uploadedAt: string;
  uploadedByEmail: string;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function mimeTypeFromExtension(ext: string): string {
  const map: Record<string, string> = {
    ".pdf": "application/pdf",
    ".png": "image/png",
    ".jpg": "image/jpeg",
    ".jpeg": "image/jpeg",
    ".gif": "image/gif",
    ".doc": "application/msword",
    ".docx": "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    ".xls": "application/vnd.ms-excel",
    ".xlsx": "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    ".csv": "text/csv",
    ".txt": "text/plain",
  };
  return map[ext.toLowerCase()] ?? "application/octet-stream";
}

function mapAdmDocumentMetadata(doc: AdmDocumentMetadata): PurchaseRequestDocument {
  const ext = doc.DocFileExtension ?? "";
  return {
    id: String(doc.Id),
    fileName: `${doc.DocTitle}${ext}`,
    fileType: mimeTypeFromExtension(ext),
    documentType: doc.DocumentType,
    uploadedAt: doc.DocDatePutin,
    uploadedByEmail: doc.UserName,
  };
}

// ---------------------------------------------------------------------------
// Fetch documents for a purchase request
// ---------------------------------------------------------------------------

export async function fetchAdmDocumentsForPurchaseRequest(
  ctx: AdmDocumentContext,
  getAccessToken: () => Promise<string | undefined>,
  lookupKey: string,
  limit = 50,
): Promise<PurchaseRequestDocument[]> {
  const headers = await getAdmAuthHeaders(getAccessToken);

  const tagFilters = JSON.stringify({
    [PR_ADM_KEY_TAG_NAME]: lookupKey,
    [ADM_TAG_NAMES.DATABASE_NAME]: ctx.dbName,
  });
  headers["X-DocumentTags"] = tagFilters;

  const params = new URLSearchParams({
    dbName: ctx.dbName,
    linkType: PR_ADM_LINK_TYPE,
    linkProduct: PR_ADM_LINK_PRODUCT,
  });

  const url = `${ctx.instanceUrl}/DocumentMetadata?${params.toString()}`;
  const response = await fetch(url, { method: "GET", headers });

  if (!response.ok) {
    throw new Error(`ADM DocumentMetadata failed (HTTP ${response.status})`);
  }

  const admDocs: AdmDocumentMetadata[] = await response.json();
  return admDocs.map(mapAdmDocumentMetadata).slice(0, limit);
}

// ---------------------------------------------------------------------------
// Upload a document for a purchase request (two-step: FileUpload → Documents)
// ---------------------------------------------------------------------------

export interface PurchaseRequestUploadMetadata {
  requestId: string;
  description?: string;
}

export async function uploadAdmDocumentForPurchaseRequest(
  ctx: AdmDocumentContext,
  getAccessToken: () => Promise<string | undefined>,
  file: File,
  metadata: PurchaseRequestUploadMetadata,
): Promise<PurchaseRequestDocument> {
  // Step 1: Upload the file
  const uploadHeaders = await getAdmAuthHeaders(getAccessToken);
  delete uploadHeaders["Content-Type"];

  const formData = new FormData();
  formData.append("file", file);

  const uploadResponse = await fetch(
    `${ctx.instanceUrl}/FileUpload`,
    { method: "POST", headers: uploadHeaders, body: formData },
  );

  if (!uploadResponse.ok) {
    throw new Error(`ADM FileUpload failed (HTTP ${uploadResponse.status})`);
  }

  const uploadJson: AdmFileUploadResponse = await uploadResponse.json();
  const identifiers = uploadJson.Identifiers ?? [];
  if (identifiers.length === 0) {
    throw new Error("ADM FileUpload returned no file entries");
  }

  const serverFileKey = identifiers[0].ServerFileKey;

  // Step 2: Create the document record with tags
  const createHeaders = await getAdmAuthHeaders(getAccessToken);

  const tagValues: AdmTagValuePair[] = [
    { TagId: ADM_TAG_IDS.DATABASE_NAME, TagValue: ctx.dbName },
    { TagId: PR_ADM_KEY_TAG_ID, TagValue: metadata.requestId },
    { TagId: ADM_TAG_IDS.ACCOUNT_CODE, TagValue: "" },
    { TagId: ADM_TAG_IDS.ACCOUNT_NAME, TagValue: "" },
    { TagId: ADM_TAG_IDS.SALES_LEDGER_PRIMARY, TagValue: "" },
    { TagId: ADM_TAG_IDS.TRANSACTION_DATE, TagValue: "" },
    { TagId: ADM_TAG_IDS.DESCRIPTION, TagValue: metadata.description || "" },
    { TagId: ADM_TAG_IDS.TRANSACTION_TYPE, TagValue: "" },
  ];

  const fileName = file.name;
  const titleWithoutExt = fileName.includes(".")
    ? fileName.substring(0, fileName.lastIndexOf("."))
    : fileName;

  const createBody = {
    ServerFileKey: serverFileKey,
    Attributes: {
      DestinationFolderId: ADM_FOLDER_ID,
      Title: titleWithoutExt,
    },
    IndexingInfo: {
      DocTypeId: PR_ADM_DOC_TYPE_ID,
      TagValues: tagValues,
    },
  };

  const createResponse = await fetch(
    `${ctx.instanceUrl}/Documents`,
    { method: "POST", headers: createHeaders, body: JSON.stringify(createBody) },
  );

  if (!createResponse.ok) {
    throw new Error(`ADM CreateDocument failed (HTTP ${createResponse.status})`);
  }

  const created: AdmCreatedDocumentResponse = await createResponse.json();

  return {
    id: String(created.Id),
    fileName,
    fileType: file.type,
    documentType: "Purchase Request",
    uploadedAt: new Date().toISOString(),
    uploadedByEmail: "",
  };
}

// ---------------------------------------------------------------------------
// Download a document
// ---------------------------------------------------------------------------

export async function downloadAdmDocument(
  ctx: AdmDocumentContext,
  getAccessToken: () => Promise<string | undefined>,
  documentId: string,
  fileName: string,
): Promise<void> {
  const headers = await getAdmAuthHeaders(getAccessToken);
  delete headers["Content-Type"];

  const response = await fetch(
    `${ctx.instanceUrl}/Documents/${documentId}/Download`,
    { method: "GET", headers },
  );

  if (!response.ok) {
    throw new Error(`ADM Download failed (HTTP ${response.status})`);
  }

  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// ---------------------------------------------------------------------------
// Delete a document
// ---------------------------------------------------------------------------

export async function deleteAdmDocument(
  ctx: AdmDocumentContext,
  getAccessToken: () => Promise<string | undefined>,
  documentId: string,
): Promise<void> {
  const headers = await getAdmAuthHeaders(getAccessToken);

  const response = await fetch(
    `${ctx.instanceUrl}/Documents/${documentId}`,
    { method: "DELETE", headers },
  );

  if (!response.ok) {
    throw new Error(`ADM Delete failed (HTTP ${response.status})`);
  }
}

// ---------------------------------------------------------------------------
// Preview a document (opens in new tab)
// ---------------------------------------------------------------------------

export async function previewAdmDocument(
  ctx: AdmDocumentContext,
  getAccessToken: () => Promise<string | undefined>,
  documentId: string,
): Promise<void> {
  const headers = await getAdmAuthHeaders(getAccessToken);
  delete headers["Content-Type"];

  const response = await fetch(
    `${ctx.instanceUrl}/Documents/${documentId}/preview`,
    { method: "GET", headers },
  );

  if (!response.ok) {
    throw new Error(`ADM Preview failed (HTTP ${response.status})`);
  }

  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  window.open(url, "_blank", "noopener,noreferrer");
}
